import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrderNotifications } from '../hooks/useOrderNotifications.jsx';
import { ClipboardIcon, CloseIcon } from './Icons.jsx';

/**
 * Floating toast that briefly appears when new orders arrive.
 * Hidden on the /orders page itself (no need to nag while user is already viewing them).
 */
export default function OrderNotificationToast() {
  const { pendingCount, hasUnread, markRead, prefs } = useOrderNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);
  const lastShownRef = useRef(0);

  useEffect(() => {
    if (!prefs.toastEnabled) return;
    if (!hasUnread || pendingCount <= lastShownRef.current) return;

    // Only show on owner-facing pages — never on public menus, order tracking,
    // login flows, or the orders page itself (already viewing them there).
    const ownerPaths = ['/dashboard', '/menus', '/settings', '/admin'];
    const path = location.pathname;
    const isOwnerPath = ownerPaths.some(p => path === p || path.startsWith(p + '/'));
    if (!isOwnerPath) {
      // Acknowledge silently so we don't fire when the user navigates back later
      lastShownRef.current = pendingCount;
      return;
    }

    setCount(pendingCount);
    setVisible(true);
    lastShownRef.current = pendingCount;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setVisible(false), 8000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [hasUnread, pendingCount, location.pathname, prefs.toastEnabled]);

  const handleView = () => {
    setVisible(false);
    markRead();
    navigate('/orders');
  };

  const handleDismiss = () => {
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '90px',
      right: '20px',
      zIndex: 1000,
      maxWidth: 'calc(100vw - 40px)',
      width: '340px',
      animation: 'almenu-toast-slide 0.25s ease-out',
    }}>
      <div style={{
        background: 'white',
        borderRadius: '14px',
        boxShadow: '0 16px 40px rgba(15, 23, 42, 0.15), 0 4px 12px rgba(15, 23, 42, 0.08)',
        border: '1px solid var(--border)',
        padding: '14px 14px 14px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        position: 'relative',
      }}>
        <div style={{
          width: '38px', height: '38px',
          borderRadius: '10px',
          background: 'var(--primary)',
          color: 'white',
          flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ClipboardIcon size={18} />
        </div>

        <div style={{ flex: 1, minWidth: 0, paddingTop: '2px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '2px' }}>
            {count === 1 ? 'New order received' : `${count} pending orders`}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '10px', lineHeight: 1.4 }}>
            A customer has placed an order. Open Orders to confirm and start preparing.
          </div>
          <button
            onClick={handleView}
            className="btn btn-primary"
            style={{ fontSize: '12px', padding: '6px 12px' }}
          >
            View orders
          </button>
        </div>

        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          style={{
            position: 'absolute',
            top: '8px', right: '8px',
            width: '24px', height: '24px',
            border: 'none', background: 'transparent',
            color: 'var(--text-3)',
            cursor: 'pointer',
            borderRadius: '6px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <CloseIcon size={14} />
        </button>
      </div>

      <style>{`
        @keyframes almenu-toast-slide {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
